import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, createContext } from "react";
import * as PusherPushNotifications from "@pusher/push-notifications-web";
import HNotification from "../components/custom/notification/HNotification.jsx";
import { NOTIFICATION_PERMISSION_STATUS, SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED } from "../constants/AppConstants.js";
import { AppContext } from "./AppContext";
import useHttp from "../hooks/useHttp";
import { checkIsInsideIframe, checkIsInternalMode } from "../pages/utils/commonUtils.js";

const BeamsContext = createContext();
export const useBeams = () => useContext(BeamsContext);

function readPushBannerDismissedFromSessionStorage() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED) === "true";
  } catch {
    return false;
  }
}

export const BeamsProvider = React.memo(({ children }) => {
  const [appProperties] = useContext(AppContext);
  const { fetchData } = useHttp();
  const beamsClientRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [pushBannerDismissedSession, setPushBannerDismissedSession] = useState(readPushBannerDismissedFromSessionStorage);
  const [notificationPermission, setNotificationPermission] = useState(() =>
    "Notification" in window ? Notification.permission : "unsupported",
  );
  const [isRequestingPushPermission, setIsRequestingPushPermission] = useState(false);
  const isEmbeddedInIframe = useMemo(() => checkIsInsideIframe(), []);
  const connectionProperties = useRef({
    osyncUserId: null,
    token: null,
    isRegistering: false, // Flag to prevent concurrent registration attempts
  });

  const getCurrentUserId = useCallback(() => connectionProperties?.current?.osyncUserId, []);

  const currentUserId = useMemo(() => {
    return appProperties?.currentUser?.osyncUserId;
  }, [appProperties]);


  // Check notification permission status
  const checkPermission = useCallback(() => {
    if ("Notification" in window) {
      const permission = Notification.permission;
      setNotificationPermission(permission);
      return permission;
    }
    setNotificationPermission(NOTIFICATION_PERMISSION_STATUS.UNSUPPORTED);
    return NOTIFICATION_PERMISSION_STATUS.UNSUPPORTED;
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications.");
      return false;
    }

    if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.GRANTED) {
      return true;
    }

    if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.DENIED) {
      console.warn("Notification permission has been denied.");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission === NOTIFICATION_PERMISSION_STATUS.GRANTED;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return false;
    }
  }, []);

  // Initialize Beams client
  const initializeBeams = useCallback(async () => {
    try {
      const instanceId = process.env.REACT_APP_PUSHER_BEAMS_INSTANCE_ID;
      if (!instanceId) {
        console.warn("Pusher Beams instance ID is not configured.");
        return false;
      }

      // Check if already initialized
      if (beamsClientRef.current) {
        console.warn("Beams client already initialized");
        return true;
      }

      // In initializeBeams
      const registration = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const beamsClient = new PusherPushNotifications.Client({
        instanceId: instanceId,
        serviceWorkerRegistration: registration,
      });

      beamsClientRef.current = beamsClient;

      // Start the client
      await beamsClient.start();
      setIsInitialized(true);
      console.warn("Beams client initialized successfully");
      return true;
    } catch (error) {
      console.error("Error initializing Beams client:", error);
      setIsInitialized(false);
      return false;
    }
  }, []);

  const registerUser = useCallback(
    async (osyncUserId) => {
      console.warn("Beams: registerUser called for:", osyncUserId);

      if (connectionProperties?.current?.isRegistering) {
        console.warn("Beams: Registration already in progress, skipping...");
        return false;
      }

      if (connectionProperties?.current?.osyncUserId === osyncUserId && isRegistered) {
        return true;
      }

      try {
        connectionProperties.current.isRegistering = true;

        if (!beamsClientRef.current || !isInitialized) {
          console.warn("Beams: client not initialized. Initializing now...");
          const initialized = await initializeBeams();
          if (!initialized) {
            return false;
          }
        }

        if (!osyncUserId) {
          console.warn("Cannot register user: osyncUserId is required");
          return false;
        }

        const hasPermission = await requestPermission();
        if (!hasPermission) {
          return false;
        }

        const existingRegisteredOsyncUserId = await beamsClientRef?.current?.getUserId();
        if (existingRegisteredOsyncUserId === osyncUserId) {
          console.warn("Beams: User already registered, skipping...");
          connectionProperties.current.osyncUserId = osyncUserId;
          setIsRegistered(true);
          return true;
        }

        const tokenProvider = {
          fetchToken: async () => {
            try {
              const response = await fetchData(`pusher/beams-auth`, "POST", null, appProperties);

              const parsed = typeof response === "string" ? JSON.parse(response) : response;

              const token = parsed?.data?.token ?? parsed?.token;

              if (!token || typeof token !== "string") {
                throw new Error("PusherBeams: Invalid token received from server");
              }

              return { token };
            } catch (error) {
              console.error("PusherBeams: fetchToken failed", error);
              throw error;
            }
          },
        };

        try {
          await beamsClientRef.current.setUserId(osyncUserId, tokenProvider);
        } catch (error) {
          // If the error is about changing userId not being allowed, clear state and retry
          if (error.message) {
            console.error("Beams: setUserId failed - userId already set. Clearing state and retrying...", error.message);
            try {
              await beamsClientRef.current.clearAllState();
              connectionProperties.current.osyncUserId = null;
              connectionProperties.current.token = null;
              setIsRegistered(false);
              // Retry setUserId after clearing
              await beamsClientRef.current.setUserId(osyncUserId, tokenProvider);
            } catch (retryError) {
              throw retryError;
            }
          } else {
            console.error("Error in setUserId:", error);
            throw error;
          }
        }

        connectionProperties.current.osyncUserId = osyncUserId;
        // Token is fetched dynamically via TokenProvider, so we don't store it here
        connectionProperties.current.token = null;
        setIsRegistered(true);

        return true;
      } catch (error) {
        console.error("Error registering user with Beams:", error);
        setIsRegistered(false);
        return false;
      } finally {
        // Always clear the registration flag
        connectionProperties.current.isRegistering = false;
      }
    },
    [isInitialized, isRegistered, initializeBeams, requestPermission, appProperties, fetchData],
  );

  // Unregister user from Beams
  const unregisterUser = useCallback(async () => {
    try {
      if (beamsClientRef.current && isRegistered) {
        await beamsClientRef.current.clearAllState();
        connectionProperties.current.osyncUserId = null;
        connectionProperties.current.token = null;
        setIsRegistered(false);
        console.warn("User unregistered from Beams");
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error unregistering user from Beams:", error);
      return false;
    }
  }, [isRegistered]);

  const shouldShowPushBanner = useMemo(() => {
    if (checkIsInternalMode()) {
      return false;
    }
    if (!("Notification" in window)) {
      return false;
    }
    if (pushBannerDismissedSession) {
      return false;
    }
    if (notificationPermission === NOTIFICATION_PERMISSION_STATUS.GRANTED) {
      return false;
    }
    return notificationPermission === NOTIFICATION_PERMISSION_STATUS.DEFAULT || notificationPermission === NOTIFICATION_PERMISSION_STATUS.DENIED;
  }, [pushBannerDismissedSession, notificationPermission]);

  const handleDismissPushBanner = useCallback(() => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY_INBOX_PUSH_BANNER_DISMISSED, "true");
    } catch {
      // ignore session storage failures
    }
    setPushBannerDismissedSession(true);
  }, []);

  const handleEnablePushNotifications = useCallback(async () => {
    setIsRequestingPushPermission(true);
    try {
      const granted = await requestPermission();
      checkPermission();
      if (!granted) {
        if (isEmbeddedInIframe) {
          appProperties?.controller?.getUrlParamsForRedirection(true, appProperties).then(function (queryParams) {
            window.open("/inbox?" + queryParams, "_blank");
          });
     
        } else if (Notification.permission === NOTIFICATION_PERMISSION_STATUS.DENIED) {
          HNotification.notify({
            type: "info",
            message: "Enable notifications",
            description: "Notifications are blocked. Please allow notifications in your browser settings.",
          });
        }
        return;
      }
      checkPermission();
      if (currentUserId) {
        await registerUser(currentUserId);
      }
    } finally {
      setIsRequestingPushPermission(false);
    }
  }, [appProperties, checkPermission, currentUserId, isEmbeddedInIframe, registerUser, requestPermission]);

  useEffect(() => {

    try {
        // Skip Beams entirely when view via admin tool
        const searchParams = new URLSearchParams(window.location.search);
        const isInternal =
            searchParams.has("internal") &&
            (searchParams.get("internal") === "true" || searchParams.get("internal") === true);
        if (isInternal) {
            console.warn("Beams: Internal mode, skipping registration");
            return;
        }
    } catch (error) {
        console.error("Error in when registering user with Beams:", error);
    }

    // Only register if we have a userId, not already registered, and not currently registering
    if (currentUserId && !isRegistered && connectionProperties.current.osyncUserId !== currentUserId && !connectionProperties.current.isRegistering) {

      initializeBeams().then((initialized) => {
        if (initialized) {
          console.warn("Beams: Adding device interest");
          registerUser(currentUserId).then(() => {
            console.warn("Beams: Registered user with Beams");
          });
        }
      });
      if (!currentUserId && isRegistered) {
        console.warn("Beams: User logged out, unregistering...");
        unregisterUser().then(() => {
          console.warn("Beams: Successfully unregistered on logout");
        });
      }
    }
  }, [currentUserId, isRegistered, initializeBeams, registerUser, unregisterUser]);

  // Check permission on mount
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const beamsContext = useMemo(
    () => ({
      beamsClient: beamsClientRef.current,
      isInitialized,
      isRegistered,
      getCurrentUserId,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
      connectionProperties: connectionProperties.current,
      shouldShowPushBanner,
      notificationPermission,
      isEmbeddedInIframe,
      isRequestingPushPermission,
      handleDismissPushBanner,
      handleEnablePushNotifications,
    }),
    [
      isInitialized,
      isRegistered,
      getCurrentUserId,
      initializeBeams,
      registerUser,
      unregisterUser,
      requestPermission,
      checkPermission,
      shouldShowPushBanner,
      notificationPermission,
      isEmbeddedInIframe,
      isRequestingPushPermission,
      handleDismissPushBanner,
      handleEnablePushNotifications,
    ],
  );

  return <BeamsContext.Provider value={beamsContext}>{children}</BeamsContext.Provider>;
});
